// Sections and sidebar placement. A section's `title` is also the chip label
// shown in global search results, so these strings must stay in step with
// what users already see there.
//
// Placement rules:
//  - `items` / `groups[].items` are registry entry IDS in sidebar order. An
//    entry can be placed in a section other than its own `section` field
//    (e.g. My Training lives under Governance in the sidebar but is a
//    'My Work' page in search) without being defined twice.
//  - Entries that are searchable but not in the sidebar simply aren't listed
//    here; the sitemap shows them grouped by their own `section` field.
//  - `check(ctx)` hides a whole section (mirrors the old section components
//    that bailed out before rendering any items).
//  - `requiredTier` + `lockNavTo`: when the tenant's tier is below it, the
//    section renders only a locked divider for site admins (the old
//    PatientTransport behaviour) and nothing for anyone else.

export const SECTIONS = [
  {
    id: 'home',
    title: 'Home',
    // Rendered without a divider, like the old top-of-sidebar items
    bare: true,
    items: ['home.dashboard', 'home.globalOverview', 'home.baseLocations'],
  },
  {
    id: 'myWork',
    title: 'My Work',
    items: [
      'myWork.availableShifts',
      'myWork.myShifts',
      'myWork.myAvailability',
      'myWork.myPay',
    ],
  },
  {
    id: 'workforce',
    title: 'Workforce',
    items: [
      'workforce.shiftApplications',
      'workforce.globalRoster',
      'workforce.staffAvailability',
      'workforce.overtimeClaims',
      'workforce.payroll',
    ],
  },
  {
    id: 'people',
    title: 'People',
    check: (ctx) => ctx.hasTeam('Personnel'),
    items: [
      'people.dashboard',
      'people.employees',
      'people.subcontractors',
      'people.applications',
      'people.mandatoryTrainingAdmin',
      'people.dbsChecks',
      'people.occupationalHealthDashboard',
    ],
  },
  {
    id: 'commandControl',
    title: 'Command & Control',
    // The old section bailed out entirely below tier 2 for everyone but site
    // admins, overriding NavItem's usual lock-icon behaviour.
    check: (ctx) => ctx.tier >= 2 || ctx.isSiteAdmin,
    items: [
      'commandControl.tacticalLogs',
      'commandControl.controlRoom',
      'commandControl.treatmentLocations',
    ],
  },
  {
    id: 'events',
    title: 'Events',
    items: ['events.calendar', 'events.proposals'],
  },
  {
    id: 'patientTransport',
    title: 'Patient Transport',
    requiredTier: 3,
    lockNavTo: '/pts/control',
    items: [
      'patientTransport.bookings',
      'patientTransport.planning',
      'patientTransport.patients',
      'patientTransport.organisations',
      'patientTransport.dispatch',
      'patientTransport.reports',
      'patientTransport.invoices',
      'patientTransport.myDuty',
    ],
  },
  {
    id: 'fleetEquipment',
    title: 'Fleet & Equipment',
    items: [
      'fleetEquipment.vehicles',
      'fleetEquipment.cleaningSchedules',
      'fleetEquipment.vdis',
      'fleetEquipment.safetyChecks',
      'fleetEquipment.defects',
      'fleetEquipment.makeReady',
      'fleetEquipment.assets',
      'fleetEquipment.reportDefect',
    ],
  },
  {
    id: 'patientRecords',
    title: 'Patient Records',
    items: [
      'patientRecords.careRecords',
      'patientRecords.recordAudits',
      'patientRecords.myDops',
      'patientRecords.imaging',
    ],
  },
  {
    id: 'governance',
    title: 'Governance',
    groups: [
      {
        id: 'everyday',
        title: 'For Everyone',
        items: [
          'governance.announcements',
          'governance.crewVault',
          'myWork.myTraining',
          'clinical.medicineAdministration',
          'clinical.speakUp',
          'governance.myDrawnUpMedicines',
          'governance.pgdLibrary',
          'governance.policyLibrary',
          'governance.sops',
        ],
      },
      {
        id: 'quality',
        title: 'Quality & Safety',
        items: [
          'governance.incidents',
          'governance.feedback',
          'governance.safetyAlerts',
          'governance.speakUpConcerns',
          'governance.riskRegister',
          'governance.audits',
        ],
      },
      {
        id: 'medicines',
        title: 'Medicines',
        items: ['governance.medsManagement', 'governance.drawnUpMedicines'],
      },
      {
        id: 'compliance',
        title: 'Compliance & CQC',
        items: [
          'governance.cqcCentre',
          'governance.governanceActions',
          'governance.kpi',
          'governance.dops',
        ],
      },
      {
        id: 'meetings',
        title: 'Meetings',
        items: ['governance.meetings'],
      },
    ],
  },
  {
    id: 'clinical',
    title: 'Clinical',
    // Search/sitemap-only section: crew tools reached from the dashboard's
    // quick links rather than the sidebar.
    items: [],
  },
  {
    id: 'customers',
    title: 'Customers',
    items: ['customers.clients'],
  },
  {
    id: 'finance',
    title: 'Finance',
    items: ['finance.centre', 'finance.invoices', 'finance.quotes'],
  },
  {
    id: 'system',
    title: 'System',
    items: ['system.siteConfiguration', 'system.smsHistory', 'system.wallboard'],
  },
  {
    id: 'help',
    title: 'Help',
    items: ['help.docs', 'help.support'],
  },
]

// Subcontractor-only accounts get a fixed short menu (plus Home and the Help
// section, which render for everyone).
export const SUBCONTRACTOR_MENU = {
  title: 'Clinical Tools',
  items: [
    'clinical.epcr',
    'clinical.medicineAdministration',
    'fleetEquipment.vdis',
    'clinical.reportIncident',
  ],
}

export const getSectionById = (id) => SECTIONS.find((s) => s.id === id)

export const getSectionTitle = (id) => getSectionById(id)?.title ?? id

// Where an entry sits in the sidebar: its section and (if any) sub-group.
// Falls back to the entry's own `section` field for searchable-only pages.
export const getEntryPlacement = (entry) => {
  for (const section of SECTIONS) {
    if (section.items?.includes(entry.id)) return { section, group: null }
    for (const group of section.groups ?? []) {
      if (group.items?.includes(entry.id)) return { section, group }
    }
  }
  return { section: getSectionById(entry.section) ?? null, group: null }
}
